import { useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Type from 'components/Type'
import Icon from 'components/Icon'
import Chip from 'components/Chip'
import Divider from 'components/Divider'
import Currency from 'components/Currency'
import Button from 'components/Button'
import Menu from 'components/Menu'
import StandardModal from 'components/CommonUI/StandardModal'
import CurrencyInput from 'components/CommonUI/CurrencyInput'
import IconAction from 'components/IconAction'
import ModalFlow from 'components/ModalFlow'
import PlaidAuthorization from 'components/ModalViews/PlaidAuthorization'
import AllSet from 'components/ModalViews/AllSet'
import AccountMenuItem from 'components/CommonUI/AccountMenuItem'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { find } from 'lodash'
import { useTypeLoad, useGet, useAgreements } from 'utils/hooks'
import { useAction } from 'utils/actions'
import { canUseAccount } from 'utils/helpers'
import { 
  GET_EXTERNAL_ACCOUNTS, 
  BOOST_ACCOUNT,
} from 'api/constants'

const DEPOSIT_OPTIONS = ['25', '50', '100']

const BoostAccount = () => {
  const dispatch = useDispatch()
  const { openModal } = useAction()
  const user = useSelector(({ global }) => global.user)
  const [activeIndex, setActiveIndex] = useState(0)
  const [boostAmount, setBoostAmount] = useState('')
  const externalAccounts = useGet(GET_EXTERNAL_ACCOUNTS, 'externalAccounts', null)
  const [accountValue, setAccountValue] = useState(user.primary_connected_account_id || -1)
  const account = useMemo(() => {
    if (!externalAccounts || !accountValue) return null
    return find(externalAccounts, { id: accountValue })
  }, [externalAccounts, accountValue])
  const isDisabled = !boostAmount.length || !canUseAccount(account)
  const {
    authLanguage,
    onNext,
  } = useAgreements({
    type: 'transfer',
    onSuccess: acceptance_id => {
      const data = { amount: boostAmount, acceptance_id }
      if (account?.id) data.account_id = account?.id
      dispatch({ type: BOOST_ACCOUNT, data })
      setActiveIndex(-1)
    },
  })

  const handleOpenPlaid = () => {
    openModal(<PlaidAuthorization />)
  }

  const handleSelectInput = () => {
    document.getElementById('boost__currency-input').select()
  }

  useTypeLoad(BOOST_ACCOUNT, data => {
    if (!data.error) setActiveIndex(2)
  })

  return (
    <ModalFlow
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      sizeX={activeIndex === 0 ? 'lg' : 'md'}
    >
      {/* BOOST ACCOUNT */}
      <Stack>
        <Stack
          p={3}
        >
          <Stack
            display="grid"
            justifyItems="center"
            mb={3}
          >
            <CurrencyInput
              id="boost__currency-input"
              align="center"
              value={boostAmount}
              onChange={setBoostAmount}
            />
            <IconAction icon="Edit" onMouseUp={handleSelectInput}>
              Change Amount
            </IconAction>
          </Stack>
          <Stack
            sx={theme => theme.internalMixins.columns({ gap: 1, justify: 'center' })}
          >
            {DEPOSIT_OPTIONS.map((option, idx) => (
              <Stack
                key={`deposit-option_${idx}`}
                width={{ xx: '70px', xs: '90px', xl: '115px' }}
              >
                <Chip
                  variant="neutral_300"
                  variantHover="primary_b500"
                  size="tall"
                  fluid
                  selectable
                  onClick={() => setBoostAmount(option)}
                >
                  $
                  {option}
                </Chip>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Divider color="neutral_200" />
        <Stack
          py={2}
          px={3}
          display="grid"
          gap={2}
          gridTemplateColumns={{
            sm: 'repeat(2, 1fr)',
          }}
          alignItems="center"
          alignContent="center"
        >
          <Menu
            placeholder="Select an Account"
            value={accountValue}
            sx={{
              minWidth: '150px',
            }}
          >
            {externalAccounts?.map((item, idx) => (
              <MenuItem
                key={`externalAccounts_${item.id}`}
                className={item.id === accountValue && 'active'}
                value={item.id}
                onClick={() => {
                  setAccountValue(item.id)
                }}
              >
                <AccountMenuItem
                  {...item}
                />
              </MenuItem>
            ))}
            <MenuItem
              onClick={handleOpenPlaid}
            >
              <Stack
                display="grid"
                gridAutoFlow="column"
                gap={1}
                alignItems="center"
                alignContent="center"
                justifyContent="flex-start"
              >
                <Icon
                  icon="Add"
                />
                Add an Account
              </Stack>
            </MenuItem>
          </Menu>
          <Button
            fluid
            disabled={isDisabled}
            onClick={() => setActiveIndex(1)}
          >
            Make a One-Time Boost
          </Button>
        </Stack>
        <Type
          variant="dataMicro"
          color="neutralCool_700"
          textAlign="center"
          mb={1}
        >
          Please allow up to 48 hours for your boost to process.
        </Type>
      </Stack>
      {/* CONFIRM */}
      <StandardModal
        icon="CelMoneyTransfer"
        title="Are you sure?"
        subtitle={
          <Type
            variant="p"
          >
            {/* By clicking ‘I understand & agree’ you authorize a one-time transfer of <Type variant="inherit" inline color="tertiary" weight="bold"><Currency amount={boostAmount} noDecimal="conditional" /></Type> between your designated accounts.
            {' '} */}
            {authLanguage?.replace('{amount}', `$${boostAmount}`)}
          </Type>
        }
        actionOne={
          <span
            onClick={onNext}
            // onClick={onConfirm}
          >
            I understand & agree
          </span>
        }
        actionTwo="Cancel"
        closeModalAction="two"
      />
      {/* COMPLETE */}
      <AllSet />
    </ModalFlow>
  )
}

BoostAccount.defaultProps = {
  drawerTitle: 'Make a One-Time Boost',
}

export default BoostAccount